import type { CSSProperties, FC, ReactElement, ReactNode } from "react"

import isString from "lodash-es/isString"

import { type TUserState, useUser } from "@/3514/hooks"
import { buildCssClassName } from "@/3514/utils"

import { avatarHelpers as helpers } from "./avatar.helpers"
import { avatarStyles as styles } from "./avatar.styles"
import { EAvatarFallbackVariant, EAvatarSize, type TAvatarProps } from "./avatar.types"

const { component: classes } = styles

const { Constructed, Img, NoImage } = helpers

const Avatar: FC<TAvatarProps> = ({
    url = undefined,
    size = EAvatarSize._35,
    alt = undefined,
    userData = { firstName: "F", lastName: "L" },
    isCurrentUser = false,
    isGuest = false,
    isExtraParty = false,
    isAccessible = false,
    isLettered = false,
    isRestCounter = false,
    fallbackVariant = EAvatarFallbackVariant.BlueColored,
    className = String(""),
    renderIcon = undefined
}: TAvatarProps): ReactElement => {
    const { user }: TUserState = useUser()

    const altToRender: string = isString(alt)
        ? alt
        : String(isCurrentUser ? `${user?.firstName} avatar` : `${userData?.firstName} avatar`)

    const urlToRender: string = isCurrentUser ? user?.photo : url

    const sizeStyles: CSSProperties = {
        height: `${size}px`,
        width: `${size}px`,
        minWidth: `${size}px`
    }

    function renderAvatar(children: ReactNode): ReactElement {
        return (
            <div className={buildCssClassName(classes.container(), { [className]: className })}>
                {children}

                {renderIcon?.()}
            </div>
        )
    }

    return isString(urlToRender)
        ? renderAvatar(
              <Img
                  alt={altToRender}
                  size={size}
                  url={urlToRender}
                  className={buildCssClassName(classes.img(), isAccessible && classes.avatar())}
                  styles={sizeStyles}
              />
          )
        : isLettered
          ? renderAvatar(
                <Constructed
                    alt={altToRender}
                    size={size}
                    isGuest={isGuest}
                    isExtraParty={isExtraParty}
                    className={buildCssClassName(classes.constructedAvatar({ variant: fallbackVariant }))}
                    styles={sizeStyles}
                    isRestCounter={isRestCounter}
                    fallbackVariant={fallbackVariant}
                    userData={{
                        ...(isCurrentUser ? { firstName: user?.firstName, lastName: user?.lastName } : userData)
                    }}
                />
            )
          : renderAvatar(<NoImage alt={altToRender} size={size} />)
}

export { Avatar }
